import { useState } from "react";
import { Mail, Smartphone, Handshake, MapPin, Clock, ArrowRight, Check } from "lucide-react";
import { useSEO } from "../lib/seo";
import { Reveal } from "../components/site/Reveal";

const EMAIL = "hello@getfetcher.in";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARDS = [
  {
    icon: Mail,
    title: "Email us",
    body: "Questions, feedback or account help — drop us a line and we'll get back to you.",
    action: EMAIL,
    href: `mailto:${EMAIL}`,
    testId: "contact-email-card",
  },
  {
    icon: Smartphone,
    title: "In-app support",
    body: "Already have an active order? The fastest help is inside the Fetcher mobile app under Support.",
    action: "Open the mobile app",
    href: "https://play.google.com/store/apps/details?id=in.getfetcher.app",
    testId: "contact-inapp-card",
  },
  {
    icon: Handshake,
    title: "Partnerships",
    body: "Run a shop or want to partner with Fetcher? We'd love to hear from you.",
    action: "Start a partnership",
    href: `mailto:${EMAIL}?subject=Partnership`,
    testId: "contact-partnership-card",
  },
];

export default function Contact() {
  useSEO({
    title: "Contact — Fetcher",
    description:
      "Get in touch with Fetcher: email hello@getfetcher.in, in-app support, and partnership enquiries. Registered office in Tamil Nadu, India.",
    path: "/contact",
  });

  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
    if (!form.name.trim() || !emailOk || !form.message.trim()) {
      setError("Please enter your name, a valid email and a message.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Something went wrong. Please try again.");
      }
      setSubmitted(true);
    } catch (err) {
      setError(typeof err.message === "string" ? err.message : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="bg-white pt-24">
      <section className="bg-gradient-to-br from-[#E86E28] via-[#D95A17] to-[#C24D0F] px-5 py-16 text-white sm:px-8">
        <div className="mx-auto max-w-5xl">
          <Reveal>
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/70">Get in touch</span>
            <h1 className="mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl">We're here to help.</h1>
            <p className="mt-4 max-w-xl text-base font-medium leading-relaxed text-white/85 sm:text-lg">
              Whether it's an order, a partnership or a general question — pick the channel that suits you best.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="px-5 py-16 sm:px-8 lg:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {CARDS.map((c, i) => (
              <Reveal
                key={c.title}
                delay={i * 0.08}
                data-testid={c.testId}
                className="flex flex-col rounded-[1.25rem] border border-slate-100 bg-white p-7 shadow-[0_8px_30px_rgba(15,23,42,0.06)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(232,110,40,0.18)]"
              >
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#FFEDD5] text-[#D95A17]">
                  <c.icon className="h-5 w-5" />
                </span>
                <h2 className="mt-5 text-xl font-extrabold tracking-tight text-slate-900">{c.title}</h2>
                <p className="mt-2 flex-1 text-sm font-medium leading-relaxed text-slate-500">{c.body}</p>
                <a
                  href={c.href}
                  target={c.href.startsWith("http") ? "_blank" : undefined}
                  rel={c.href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={`${c.testId}-action`}
                  className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-[#D95A17] transition hover:text-[#E86E28]"
                >
                  {c.action}
                </a>
              </Reveal>
            ))}
          </div>

          {/* Contact form */}
          <Reveal data-testid="contact-form-section" className="mt-10 overflow-hidden rounded-[1.5rem] border border-slate-100 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.06)] md:grid md:grid-cols-[0.9fr_1.1fr]">
            <div className="bg-gradient-to-br from-[#3A2E7E] to-[#2a2160] p-8 text-white">
              <h2 className="text-2xl font-extrabold tracking-tight sm:text-3xl">Send us a message</h2>
              <p className="mt-3 text-sm font-medium leading-relaxed text-white/70">
                Fill in the form and your message lands straight in our inbox at{" "}
                <span className="font-bold text-white">{EMAIL}</span>. We'll reply within 24 hours.
              </p>
            </div>

            <div className="p-6 sm:p-8">
              {submitted ? (
                <div data-testid="contact-success" className="flex h-full flex-col items-center justify-center py-6 text-center">
                  <span className="grid h-14 w-14 place-items-center rounded-full bg-[#22c55e] text-white">
                    <Check className="h-7 w-7" strokeWidth={3} />
                  </span>
                  <h3 className="mt-4 text-xl font-extrabold tracking-tight text-slate-900">Message sent!</h3>
                  <p className="mt-2 max-w-sm text-sm font-medium leading-relaxed text-slate-500">
                    Thanks, {form.name.split(" ")[0] || "there"} — we've received your message and will get back to you soon.
                  </p>
                  <button
                    type="button"
                    data-testid="contact-reset"
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: "", email: "", message: "" });
                    }}
                    className="mt-5 rounded-full bg-slate-900 px-6 py-3 text-sm font-extrabold text-white transition hover:-translate-y-0.5 hover:bg-black"
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={onSubmit} className="flex flex-col gap-4">
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Your name</span>
                    <input
                      data-testid="contact-input-name"
                      type="text"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Priya Sharma"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</span>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>
                  <label className="flex flex-col gap-2">
                    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Message</span>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.message}
                      onChange={update("message")}
                      rows={4}
                      placeholder="How can we help?"
                      className="resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-base font-medium text-slate-900 outline-none transition focus:border-[#E86E28] focus:ring-2 focus:ring-[#E86E28]/20"
                    />
                  </label>

                  {error && (
                    <p data-testid="contact-error" className="text-sm font-semibold text-red-600">
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={submitting}
                    className="group mt-1 inline-flex items-center justify-center gap-3 rounded-full bg-[#E86E28] px-8 py-4 text-base font-extrabold text-white shadow-[0_14px_40px_rgba(232,110,40,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#D95A17] disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {submitting ? "Sending…" : "Send message"}
                    {!submitting && <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          {/* Office + SLA */}
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2">
            <Reveal data-testid="contact-office" className="rounded-[1.25rem] bg-[#0B0F19] p-7 text-white">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-[#E86E28]">
                <MapPin className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-xl font-extrabold tracking-tight">Registered office</h3>
              <p className="mt-2 text-sm font-medium leading-relaxed text-white/60">
                Fetcher — Tamil Nadu, India.
              </p>
            </Reveal>

            <Reveal data-testid="contact-sla" delay={0.1} className="rounded-[1.25rem] bg-[#3A2E7E] p-7 text-white">
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-white/10 text-[#E86E28]">
                <Clock className="h-5 w-5" />
              </span>
              <h3 className="mt-5 text-xl font-extrabold tracking-tight">Response times</h3>
              <ul className="mt-2 space-y-2 text-sm font-medium leading-relaxed text-white/70">
                <li><strong className="text-white">Order support:</strong> within 30 minutes during business hours.</li>
                <li><strong className="text-white">General enquiries:</strong> within 24 hours.</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
